import React, { useEffect, useState } from "react";

import { AvatarShape } from "./avatar-constants";
import { getAvatarColor, getInitialsFromText } from "./avatar-initials";

import "./avatar.css";

/**
 * A photo with an initials fallback.
 *
 * Deliberately dumb about what counts as a photo: the caller decides. Consuming apps can have their
 * own "no photo" sentinels (a placeholder filename, a directory URL) that this library has no way to
 * recognise, so they pass Src={null} once they have made that call themselves.
 *
 * Props:
 *   Src         string  photo URL; null/empty renders initials instead
 *   Initials    string  precomputed initials; derived from Seed when omitted
 *   Seed        string  hash and initials source. Prefer something unique per person (an email)
 *   Size        number  one of AvatarSize; omit to let Class size the box
 *   Shape       number  one of AvatarShape; defaults to RoundedSquare
 *   Background  string  overrides the hashed colour behind the initials
 *   Alt         string  set ONLY when the avatar is the sole identifier; see below
 *   Class       string  extra classes, so callers can reuse their own sizing rules
 *   DataTestId  string  emits `${DataTestId}.avatar` and `${DataTestId}.initials`
 *
 * Accessibility: omit Alt when the avatar sits next to a visible name. The photo then gets alt=""
 * and the initials get aria-hidden, so a screen reader announces the name once instead of twice.
 * Pass Alt only where nothing else identifies the person.
 */
function Avatar(props) {
    const { Src, Seed, Size, Shape, Background, Alt, Class, DataTestId } = props;
    const [failed, setFailed] = useState(false);

    // A newly uploaded photo deserves a fresh attempt.
    useEffect(() => {
        setFailed(false);
    }, [Src]);

    const initials = props.Initials === undefined || props.Initials === null
        ? getInitialsFromText(Seed)
        : props.Initials;

    const getClass = (variant) => {
        let className = `eone-ui-avatar eone-ui-avatar-${variant}`;
        if (Shape === AvatarShape.Circle) className += " eone-ui-avatar-circle";
        if (Size) className += " eone-ui-avatar-sized";
        if (Class) className += ` ${Class}`;
        return className;
    };

    // A custom property rather than width/height, so the initials font size can scale with the box.
    const sizeStyle = Size ? { "--eone-ui-avatar-size": `${Size}px` } : undefined;

    if (Src && !failed) {
        return (
            <img
                className={getClass("photo")}
                style={sizeStyle}
                src={Src}
                alt={Alt || ""}
                onError={() => setFailed(true)}
                data-testid={DataTestId ? `${DataTestId}.avatar` : undefined} />
        );
    }

    return (
        <div
            className={getClass("initials")}
            style={{ ...sizeStyle, background: Background || getAvatarColor(Seed || initials) }}
            role={Alt ? "img" : undefined}
            aria-label={Alt || undefined}
            aria-hidden={Alt ? undefined : "true"}
            data-testid={DataTestId ? `${DataTestId}.initials` : undefined}>
            {initials}
        </div>
    );
}

export { Avatar };
